/** @file Mesh form: read-only stats and binding above the mesh's generated forms (skinned-only ones hidden). */
import type { EntityViewProps, FormKind } from './formKindOf';
import { EntityForms } from './EntityForms';
import { ENTITY_FORMS } from './entityForms.data';
import { meshStats } from './meshStats';

const MESH: FormKind = 'mesh';
const SKINNED_ONLY: ReadonlySet<string> = new Set(['mesh.smoothWeights', 'mesh.rebindMesh']);

/** `vertices` is the selected-vertex count when the Inspector shows a vertex selection (weights live in the Weights panel, W3-42). */
export type MeshFormProps = Omit<EntityViewProps, 'kind'> & { vertices?: number };

/** Rigid meshes name the bone they follow; skinned meshes count their weighted bones; a line points at the Weights panel. */
export function MeshForm({ doc, id, t, vertices }: MeshFormProps) {
  const stats = meshStats(doc, id);
  const picked = vertices === undefined ? null : <p className="p2-inspector-note">{t('panels.inspector.verticesCount', { n: vertices })}</p>;
  if (stats === null) return picked;
  const forms = ENTITY_FORMS.mesh.forms.filter((command) => stats.skinned || !SKINNED_ONLY.has(command));
  const binding = stats.skinned ? t('panels.inspector.mesh.skinned', { n: stats.bones }) : t('panels.inspector.mesh.rigid', { bone: stats.follows ?? '—' });
  const intro = (
    <>
      <p className="p2-inspector-note">{t('panels.inspector.mesh.stats', { vertices: stats.vertices, triangles: stats.triangles, hull: stats.hull })}</p>
      <p className="p2-inspector-note">{binding}</p>
      {picked}
      <p className="p2-inspector-note">{t('panels.inspector.mesh.weightsHint')}</p>
    </>
  );
  return <EntityForms doc={doc} kind={MESH} id={id} t={t} forms={forms} intro={intro} />;
}
